import type { HardwareDevice } from '../services/hardwareService';

/** ID projektu do zapytania GET /hardware?project_id= — jak w widoku mobilnym (złożone `id` = „nr nazwa”). */
export function resolveProjectIdForHardwareQuery(
  project: { project_id?: string | null; id?: string | null } | null | undefined
): string | null {
  if (!project) return null;
  const pid = String(project.project_id || '').trim();
  if (pid) return pid;
  const head = String(project.id || '').trim().split(/\s+/)[0] || '';
  return head || null;
}

/**
 * Na HTTPS (produkcja) strumień idzie przez backend — unikamy mixed content (HTTP z Pi).
 * Lokalnie (http://localhost:3000): domyślnie bez proxy — przeglądarka woła Pi bezpośrednio (wymaga dostępu do IP/Tailscale).
 * Jeśli Pi ma być widoczny tylko z backendu: w `.env.local` ustaw `REACT_APP_CAMERA_HLS_PROXY=true` i uruchom API z dostępem do Pi.
 * Wymuszenie: REACT_APP_CAMERA_HLS_PROXY=true|false
 */
export function shouldUseHardwareHlsProxy(): boolean {
  const forced = String(process.env.REACT_APP_CAMERA_HLS_PROXY || '').toLowerCase();
  if (forced === 'true') return true;
  if (forced === 'false') return false;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') return true;
  const api = String(process.env.REACT_APP_API_URL || '').toLowerCase();
  return api.startsWith('https://');
}

/** Safari / natywne HLS: brak nagłówka Authorization — token w query (tylko ścieżki proxy). */
export function appendCameraStreamAuthIfNeeded(url: string): string {
  if (!url?.trim() || !shouldUseHardwareHlsProxy()) return url;
  if (!url.includes('/hardware/') || !url.includes('/hls/')) return url;
  try {
    const token = localStorage.getItem('auth_token');
    if (!token) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}access_token=${encodeURIComponent(token)}`;
  } catch {
    return url;
  }
}

/** Host do URL HLS (bez proxy): ten sam priorytet co proxy na backendzie — najpierw Tailscale. */
export function resolveHardwareStreamHost(d: HardwareDevice): string {
  const ip = (d.device_ip || '').trim();
  const tail = (d.tailscale_ip || '').trim();
  if (tail) return tail;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(ip)) return ip;
  if (ip.includes(':')) return ip;
  return ip;
}

/**
 * URL kamery „magazyn” gdy brak wpisu w /hardware dla projektu.
 * Kolejność: localStorage → REACT_APP_CAMERA_MAGAZYN_CAM{n}_URL → REACT_APP_CAMERA_MAGAZYN_HTTP_BASE → ''.
 * Nie ustawiamy tu stałego LAN — stary 192.168.1.140 powodował timeouty na produkcji i migotanie miniaturek.
 */
export function globalMagazynCamUrl(camIndex1Based: number): string {
  try {
    const override = localStorage.getItem(`camera:magazyn:cam${camIndex1Based}:url`);
    if (override && override.trim()) return override.trim();
  } catch {
    /* ignore */
  }
  const envKey = `REACT_APP_CAMERA_MAGAZYN_CAM${camIndex1Based}_URL` as keyof NodeJS.ProcessEnv;
  const envUrl = (process.env[envKey] as string | undefined)?.trim?.() || '';
  if (envUrl) return envUrl;
  const base = String(process.env.REACT_APP_CAMERA_MAGAZYN_HTTP_BASE || '')
    .trim()
    .replace(/\/$/, '');
  if (base) return `${base}/cam${camIndex1Based}/index.m3u8`;
  return '';
}

export function defaultFourMagazynUrls(): string[] {
  return [1, 2, 3, 4].map(globalMagazynCamUrl);
}

export type BuildHardwareHlsOptions = {
  /** true = https://API/.../hardware/:id/hls/cam1/index.m3u8 (proxy na backendzie) */
  useProxy?: boolean;
  /** Bazowy URL API, np. https://domena.pl/api (bez końcowego /) */
  apiBase?: string;
};

/**
 * Buduje adresy HLS z rekordów hardware (kolejność: id rosnąco).
 * Tryb bez proxy: http://HOST:PORT/camN/index.m3u8
 * Tryb proxy (produkcja HTTPS): {apiBase}/hardware/:id/hls/camN/index.m3u8
 */
export function buildHardwareHlsUrls(
  devices: HardwareDevice[],
  maxStreams = 4,
  options?: BuildHardwareHlsOptions
): string[] {
  const port = (process.env.REACT_APP_HARDWARE_CAMERA_HLS_PORT || '').trim() || '8888';
  const useProxy = Boolean(options?.useProxy && options?.apiBase);
  const apiBase = (options?.apiBase || '').replace(/\/$/, '');
  const urls: string[] = [];
  const sorted = [...devices].sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  for (const d of sorted) {
    const n = Math.max(0, Number(d.camera_count) || 0);
    if (n < 1) continue;
    const host = resolveHardwareStreamHost(d);
    if (!host && !useProxy) continue;
    for (let c = 1; c <= n && urls.length < maxStreams; c++) {
      if (useProxy && apiBase) {
        urls.push(`${apiBase}/hardware/${d.id}/hls/cam${c}/index.m3u8`);
      } else {
        urls.push(`http://${host}:${port}/cam${c}/index.m3u8`);
      }
    }
  }
  return urls;
}

export function padCameraUrlsToFour(urls: string[]): string[] {
  const out = [...urls];
  while (out.length < 4) out.push('');
  return out.slice(0, 4);
}
